import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  makeSelectProcessing,
  makeSelectHelpfulResult,
} from 'containers/Resources/selectors';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Slide from '@material-ui/core/Slide';
import _get from 'lodash/get';
import _has from 'lodash/has';
import { useDispatch, useSelector } from 'react-redux';
import { voteHelpfulResource } from 'containers/Resources/actions';
import ClassNames from 'classnames';
import LinearProgress from '@material-ui/core/LinearProgress';
import DislikeIcon from 'components/ResourcePageLeftPanel/images/dislike.svg';
import LikeIcon from 'components/ResourcePageLeftPanel/images/like.svg';
import LoadingIndicator from 'components/LoadingIndicator';
import {
  makeSelectClientDetails,
  makeSelectHelpfulLocation,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import {
  setHelpfulLocation,
  showFloatingHelpfulAgain,
} from 'containers/Main/actions';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import { createStructuredSelector } from 'reselect';
import CloseIcon from './images/close.svg';
import Questions from './Questions';
import Success from './Success';
import { getTypeMapping } from './utils';
import { DEFAULT_SURVEY, DEFAULT_MIN_COUNT } from './constants';

const useStyles = makeStyles(theme => ({
  root: {
    width: 366,
    position: 'fixed',
    left: 20,
    bottom: 72,
    zIndex: 999,
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    opacity: '0.99',
    border: '1px solid #E3E3E3',
    boxShadow: '0 2px 14px 0 rgba(0,0,0,0.10)',
    borderRadius: 10,
    padding: 26,
    [theme.breakpoints.up('1200')]: {
      right: '8%',
    },
    [theme.breakpoints.up('1400')]: {
      right: '10%',
    },
    [theme.breakpoints.down('480')]: {
      bottom: 0,
      right: 0,
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      borderRadius: 0,
      width: '100%',
    },
  },
  title: {
    fontFamily: 'PoppinsSemiBold',
    fontSize: 21,
    direction: ({ textDirection }) => textDirection,
    textAlign: ({ textDirection }) => (textDirection === 'rtl' ? 'left' : ''),
  },
  btn: {
    width: 152,
    height: 56,
    border: '1px solid #D8D8D8',
    borderRadius: 10,
    fontSize: 17,
    [theme.breakpoints.down('xs')]: {
      margin: '0 10px',
    },
  },
  btnIcon: {
    marginRight: 6,
  },
  info: {
    fontSize: 14,
  },
  muted: {
    color: '#A6A6A6',
  },
  close: {
    position: 'absolute',
    right: 25,
    top: 25,
  },
  btnWrapper: {
    justifyContent: 'space-between',
    padding: '15px 0',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
  },
  linearRoot: {
    width: 80,
    height: 4,
    borderRadius: 3,
    marginBottom: 15,
    '& > div': {
      background: '#ECF3F5',
      borderRadius: 3,
    },
  },
  activeBtnLike: {
    borderColor: '#01619B',
    color: '#01619B',
  },
  activeBtnDislike: {
    borderColor: '#E14B4B',
    color: '#E14B4B',
  },
  loadingWrapper: {
    zIndex: 5,
    height: '100%',
    width: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  dontShowAgain: {
    cursor: 'pointer',
    fontWeight: '400',
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
}));

const stateSelector = createStructuredSelector({
  processing: makeSelectProcessing(),
  result: makeSelectHelpfulResult(),
  helpfulLocation: makeSelectHelpfulLocation(),
  clientDetails: makeSelectClientDetails(),
});

function ResourcePageHelpful(props) {
  const textDirection = useSelector(makeSelectTextDirection());
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('xs'));
  const classes = useStyles({ textDirection });

  const {
    type,
    show,
    toggle,
    ratings,
    item: {
      fields: { title, slug, name, article },
      sys: { id },
    },
  } = props;
  const isPositive = ratings.prevRating;
  const positive = (ratings && ratings.up) || 0;
  const [submitted, setSubmitted] = useState(false);
  const [voted, setVoted] = useState(false);
  const [step, setStep] = useState('default');
  const dispatch = useDispatch();
  const { processing, result, helpfulLocation, clientDetails } = useSelector(
    stateSelector,
  );

  const [siteCopy, resourceItem] = useSiteCopySelector([
    'resource-helpful',
    'resource-item-page',
  ]);
  const resourceHelpfulCopy = _get(siteCopy, 'pageCopy', {});

  const surveyConfig = _get(resourceHelpfulCopy, 'survey', DEFAULT_SURVEY);
  const surveyMinCount = _get(
    resourceHelpfulCopy,
    'options.minCount',
    DEFAULT_MIN_COUNT,
  );

  const total =
    ((ratings && ratings.up) || 0) + ((ratings && ratings.down) || 0);

  const hasRating =
    (surveyMinCount[type] || surveyMinCount.default || 5) < total;
  const hasPrevRatings = !!_get(ratings, 'up') || !!_get(ratings, 'down');
  useEffect(() => {
    if (!submitted) {
      const liked = hasPrevRatings && isPositive === true;
      const disLiked = hasPrevRatings && isPositive === false;
      if (((!voted && helpfulLocation === 'menu') || voted) && show) {
        setStep(liked ? 'like' : disLiked ? 'dislike' : 'default');
      }
    }
  }, [ratings, submitted, helpfulLocation, voted, show]);

  useEffect(() => {
    if (!show) {
      setSubmitted(false);
      setStep('default');
    }
  }, [show]);

  useEffect(() => {
    if (_has(result, 'error')) {
      if (result.error) {
        toast(
          'An error occurred while trying to save your feedback. Please, try again.',
          { type: 'error' },
        );
      } else if (submitted) {
        setStep('success');
      }
    }
  }, [result, submitted]);

  const closeFloat = () => {
    toggle(true);
    Mixpanel.track('Was This Helpful - Dismissed', {
      path: window.location.pathname,
      type,
      name: title || name || article,
      location: helpfulLocation || 'slide-in',
      slug,
      entryId: id,
    });
  };

  const vote = (value, data = {}, submit) => {
    if (submit) {
      setSubmitted(true);
      dispatch(setHelpfulLocation(''));
    } else {
      setVoted(true);
    }
    const answer = typeof value === 'boolean' ? value : value === 'yes';
    const secondaryOption =
      _get(data, 'textValue') ||
      _get(data, 'secondaryValueReadable') ||
      _get(data, 'secondaryValue');
    if (secondaryOption && secondaryOption.includes('Other')) {
      const index = secondaryOption.indexOf('Other');
      secondaryOption[index] = `Other - ${_get(data, 'textualValue', '')}`;
    }

    Mixpanel.track('Resource - Was this helpful', {
      path: window.location.pathname,
      type,
      name: title || name || article,
      location: helpfulLocation || 'slide-in',
      answer,
      slug,
      secondaryOption,
      entryId: id,
    });
    dispatch(
      voteHelpfulResource({
        resourceID: id,
        isThumbUp: answer,
        data,
        name: title || name || article,
        slug,
        type,
      }),
    );
  };

  const onDontShowAgainClick = () => {
    dispatch(showFloatingHelpfulAgain(false));
    toggle(true);
    Mixpanel.track('Was This Helpful - Skip Clicked', {
      path: window.location.pathname,
      type,
      name: title || name || article,
      location: helpfulLocation || 'slide-in',
      slug,
      entryId: id,
    });
  };

  const content = () => {
    switch (step) {
      case 'like':
        return (
          <Questions
            submit={vote}
            data={_get(surveyConfig, '1')}
            type="like"
            otherPlaceholder={_get(
              resourceHelpfulCopy,
              'labels.otherPlaceholder',
            )}
            submitLabel={_get(resourceHelpfulCopy, 'labels.submitLabel')}
          />
        );
      case 'dislike':
        return (
          <Questions
            submit={vote}
            data={_get(surveyConfig, '2')}
            type="dislike"
            otherPlaceholder={_get(
              resourceHelpfulCopy,
              'labels.otherPlaceholder',
            )}
            submitLabel={_get(resourceHelpfulCopy, 'labels.submitLabel')}
          />
        );
      case 'success':
        return <Success data={_get(surveyConfig, '4')} />;
      default:
        return (
          <>
            {hasRating && (
              <Box component="div">
                <Typography className={classes.info}>
                  {positive} {_get(resourceHelpfulCopy, 'labels.outOf')} {total}{' '}
                  <span className={classes.muted}>
                    {_get(resourceHelpfulCopy, 'labels.foundThisHelpful')}
                  </span>
                </Typography>
              </Box>
            )}
            <Typography
              className={classes.dontShowAgain}
              variant="caption"
              onClick={onDontShowAgainClick}
            >
              {_get(resourceHelpfulCopy, 'labels.dontShowAgain')}
            </Typography>
          </>
        );
    }
  };

  const getValue = () => {
    switch (step) {
      case 'like':
      case 'dislike':
        return 60;
      case 'success':
        return 100;
      default:
        return 30;
    }
  };

  if (!show) return null;

  if (isMobile && _get(clientDetails, 'metadata.hideMobileHelpful'))
    return null;

  return (
    <Slide direction="right" in={show} mountOnEnter unmountOnExit>
      <Box component="div" className={classes.root}>
        {processing && (
          <div className={classes.loadingWrapper}>
            <LoadingIndicator />
          </div>
        )}
        <ButtonBase
          aria-label="Close"
          onClick={closeFloat}
          disableRipple
          disableTouchRipple
          className={classes.close}
        >
          <img src={CloseIcon} alt="close" />
        </ButtonBase>

        <Box component="div" className={classes.linearRoot}>
          <LinearProgress variant="determinate" value={getValue()} />
        </Box>
        {step === 'default' && (
          <Box component="div">
            <Typography className={classes.title}>
              {_get(surveyConfig, '0.header').replace(
                '<ResourceType>',
                _get(
                  resourceItem,
                  ['pageCopy', 'resourceCardTypeMapping', getTypeMapping(type)],
                  getTypeMapping(type),
                ),
              )}
            </Typography>
          </Box>
        )}
        {step !== 'success' && (
          <Box component="div" display="flex" className={classes.btnWrapper}>
            <ButtonBase
              className={ClassNames(classes.btn, {
                [classes.activeBtnLike]: hasPrevRatings && isPositive === true,
              })}
              onClick={() => vote(true)}
            >
              <img src={LikeIcon} alt="Like" className={classes.btnIcon} />
              {_get(surveyConfig, '0.options.0.label')}
            </ButtonBase>
            <ButtonBase
              className={ClassNames(classes.btn, {
                [classes.activeBtnDislike]:
                  hasPrevRatings && isPositive === false,
              })}
              onClick={() => vote(false)}
            >
              <img src={DislikeIcon} alt="Like" className={classes.btnIcon} />
              {_get(surveyConfig, '0.options.1.label')}
            </ButtonBase>
          </Box>
        )}

        {content()}
      </Box>
    </Slide>
  );
}
ResourcePageHelpful.propTypes = {
  show: PropTypes.bool,
  toggle: PropTypes.func,
  ratings: PropTypes.object,
  item: PropTypes.object,
  type: PropTypes.string,
};

export default ResourcePageHelpful;
